import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Plus, Clock, MapPin, Users, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { db } from '../../lib/db';
import { useCalendarEvents } from '../../hooks/useDatabase';
import { useAuthStore } from '../../store/authStore';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isToday } from 'date-fns';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export default function CalendarPage() {
  const { user } = useAuthStore();
  const { createEvent, deleteEvent, loading } = useCalendarEvents();
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [newEvent, setNewEvent] = useState({ title: '', type: 'meeting', date: '', time: '', location: '', description: '' });

  useEffect(() => { loadEvents(); }, [user]);

  const loadEvents = async () => {
    const [calendarData, appraisals, goals, users] = await Promise.all([
      db.calendarEvents.toArray(),
      db.appraisals.toArray(),
      db.goals.toArray(),
      db.users.toArray()
    ]);

    const role = user?.role;

    const relevantUserIds = (() => {
      if (role === 'admin') return users.map(u => u.id);
      if (role === 'supervisor') return [user?.id, ...users.filter(u => u.supervisorId === user?.id).map(u => u.id)];
      return [user?.id];
    })();

    const derivedAppraisalEvents = appraisals
      .filter(a => {
        if (!a.dueDate) return false;
        if (!relevantUserIds.includes(a.userId)) return false;
        if (role === 'supervisor' && a.visibleToSupervisor === false) return false;
        return true;
      })
      .map(a => ({
        id: `appraisal-${a.id}`,
        title: `Appraisal due: ${a.title}`,
        type: 'appraisal',
        startDate: a.dueDate,
        description: 'Appraisal due date'
      }));

    const derivedGoalEvents = goals
      .filter(g => g.dueDate && relevantUserIds.includes(g.userId))
      .map(g => ({
        id: `goal-${g.id}`,
        title: `Goal due: ${g.title}`,
        type: 'goal',
        startDate: g.dueDate,
        description: g.description || 'Goal due date'
      }));

    setUsers(users);
    setEvents([...calendarData, ...derivedAppraisalEvents, ...derivedGoalEvents]);
  };

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  const getEventsForDay = (date) => events.filter(event => isSameDay(new Date(event.startDate), date));

  const upcomingEvents = events.filter(e => new Date(e.startDate) >= new Date()).sort((a, b) => a.startDate - b.startDate).slice(0, 5);

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'meeting': return 'bg-violet-500';
      case 'deadline': return 'bg-rose-500';
      case 'review': return 'bg-teal-500';
      case 'appraisal': return 'bg-amber-500';
      case 'goal': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const handleAddEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      toast.error('Please fill in title and date');
      return;
    }
    try {
      const startDate = new Date(`${newEvent.date}T${newEvent.time || '09:00'}`).getTime();
      await createEvent({
        title: newEvent.title,
        description: newEvent.description,
        type: newEvent.type,
        startDate,
        endDate: startDate + 3600000,
        location: newEvent.location,
        createdBy: user.id,
        participants: [user.id]
      });
      toast.success('Event created successfully!');
      setShowAddModal(false);
      setNewEvent({ title: '', type: 'meeting', date: '', time: '', location: '', description: '' });
      loadEvents();
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  const getCreatorName = (creatorId) => {
    if (!creatorId) return 'Unknown';
    const creator = users.find(u => u.id === creatorId);
    if (!creator) return 'Unknown';
    const first = creator.profile?.firstName || '';
    const last = creator.profile?.lastName || '';
    return `${first} ${last}`.trim() || creator.email || 'Unknown';
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    const isDerived = selectedEvent.id.startsWith('appraisal-') || selectedEvent.id.startsWith('goal-');
    if (isDerived) return;
    try {
      await deleteEvent(selectedEvent.id);
      setSelectedEvent(null);
      loadEvents();
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Calendar</h1>
          <p className="text-gray-500 mt-1">Manage your schedule and events</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="bg-teal-600 hover:bg-teal-700 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add Event
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="border-0 shadow-sm lg:col-span-2">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">{format(currentDate, 'MMMM yyyy')}</CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setCurrentDate(subMonths(currentDate, 1))}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Today</Button>
                <Button variant="outline" size="icon" onClick={() => setCurrentDate(addMonths(currentDate, 1))}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-1 mb-2">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">{day}</div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {Array.from({ length: monthStart.getDay() }).map((_, i) => (<div key={`empty-start-${i}`} className="h-24 p-1" />))}
              {daysInMonth.map(day => {
                const dayEvents = getEventsForDay(day);
                const isCurrentDay = isToday(day);
                const isSelected = selectedDate && isSameDay(day, selectedDate);
                return (
                  <div key={day.toISOString()} onClick={() => setSelectedDate(day)}
                    className={`h-24 p-1 border rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'border-teal-500 bg-teal-50' :
                      isCurrentDay ? 'border-teal-200 bg-teal-50/50' :
                      'border-gray-100 hover:border-gray-200 hover:bg-gray-50'
                    }`}>
                    <div className={`text-sm font-medium mb-1 ${isCurrentDay ? 'text-teal-600' : 'text-gray-700'}`}>{format(day, 'd')}</div>
                    <div className="space-y-1">
                      {dayEvents.slice(0, 2).map(event => (
                        <div key={event.id} onClick={(e) => { e.stopPropagation(); setSelectedEvent(event); }}
                          className={`text-xs px-1 py-0.5 rounded truncate text-white ${getEventTypeColor(event.type)}`}>
                          {event.title}
                        </div>
                      ))}
                      {dayEvents.length > 2 && <div className="text-xs text-gray-500">+{dayEvents.length - 2} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm">
          <CardHeader><CardTitle className="text-lg">Upcoming Events</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {upcomingEvents.length > 0 ? upcomingEvents.map(event => (
              <div key={event.id} onClick={() => setSelectedEvent(event)} className="p-3 border border-gray-100 rounded-lg hover:border-gray-200 cursor-pointer">
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-medium text-gray-900 text-sm">{event.title}</h4>
                  <Badge variant="secondary" className="text-xs capitalize">{event.type}</Badge>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center gap-2 text-xs text-gray-500"><Clock className="w-3 h-3" />{format(new Date(event.startDate), 'MMM d, h:mm a')}</div>
                  {event.location && <div className="flex items-center gap-2 text-xs text-gray-500"><MapPin className="w-3 h-3" />{event.location}</div>}
                </div>
              </div>
            )) : <p className="text-sm text-gray-500 text-center py-4">No upcoming events</p>}
          </CardContent>
        </Card>
      </div>

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Event</DialogTitle>
            <DialogDescription>Create a new calendar event</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Title *</Label><Input value={newEvent.title} onChange={(e) => setNewEvent({...newEvent, title: e.target.value})} placeholder="Event title" /></div>
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newEvent.type} onValueChange={(v) => setNewEvent({...newEvent, type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="meeting">Meeting</SelectItem>
                  <SelectItem value="deadline">Deadline</SelectItem>
                  <SelectItem value="review">Review</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Date *</Label><Input type="date" value={newEvent.date} onChange={(e) => setNewEvent({...newEvent, date: e.target.value})} /></div>
              <div className="space-y-2"><Label>Time</Label><Input type="time" value={newEvent.time} onChange={(e) => setNewEvent({...newEvent, time: e.target.value})} /></div>
            </div>
            <div className="space-y-2"><Label>Location</Label><Input value={newEvent.location} onChange={(e) => setNewEvent({...newEvent, location: e.target.value})} placeholder="Conference Room A" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={newEvent.description} onChange={(e) => setNewEvent({...newEvent, description: e.target.value})} placeholder="Event description..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button onClick={handleAddEvent} disabled={loading} className="bg-teal-600 hover:bg-teal-700 text-white">{loading ? 'Creating...' : 'Add Event'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{selectedEvent?.title}</DialogTitle></DialogHeader>
          {selectedEvent && (
            <div className="space-y-4">
              <Badge variant="secondary" className="capitalize">{selectedEvent.type}</Badge>
                  {user?.role === 'admin' && selectedEvent.createdBy && (
                    <p className="text-sm text-gray-500">Created by {getCreatorName(selectedEvent.createdBy)}</p>
                  )}
              <p className="text-gray-600">{selectedEvent.description}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm text-gray-500"><Clock className="w-4 h-4" />{format(new Date(selectedEvent.startDate), 'MMMM d, yyyy h:mm a')}</div>
                {selectedEvent.location && <div className="flex items-center gap-2 text-sm text-gray-500"><MapPin className="w-4 h-4" />{selectedEvent.location}</div>}
              </div>
            </div>
          )}
          <DialogFooter>
            {selectedEvent && (user?.role === 'admin' || selectedEvent.createdBy === user?.id) && !selectedEvent.id.startsWith('appraisal-') && !selectedEvent.id.startsWith('goal-') && (
              <Button variant="destructive" onClick={handleDeleteEvent} className="gap-2">
                <Trash2 className="w-4 h-4" /> Delete
              </Button>
            )}
            <Button variant="outline" onClick={() => setSelectedEvent(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
